import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { createTournament, getTournaments } from '../api';
import { useAuth } from '../auth';
import { fmtDate, useT } from '../i18n';
import PageHeader from '../components/PageHeader';
import Notice from '../components/Notice';
import type { TournamentFormat, TournamentInput, TournamentStatus } from '../types';

const STATUS_CLS: Record<TournamentStatus, string> = { open: 'bg-court-500/15 text-court-400', live: 'bg-ball-500/15 text-ball-300', done: 'bg-ink-800 text-dim' };

export default function Tournaments() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['tournaments'], queryFn: getTournaments });
  const [showNew, setShowNew] = useState(false);
  const create = useMutation({
    mutationFn: createTournament,
    onSuccess: () => { setShowNew(false); qc.invalidateQueries({ queryKey: ['tournaments'] }); qc.invalidateQueries({ queryKey: ['home'] }); },
  });
  const list = q.data?.tournaments ?? [];

  return (
    <>
      <PageHeader eyebrow="BB Commercial" title={t('t.title')} subtitle={t('t.sub')}
        action={user?.role === 'admin' ? <button className="btn-primary" onClick={() => setShowNew((v) => !v)}>{t('t.new')}</button> : undefined} />
      {showNew && (
        <div className="card p-5 mb-6">
          <TournamentForm busy={create.isPending} error={create.error?.message} onSubmit={(i) => create.mutate(i)} onCancel={() => setShowNew(false)} />
        </div>
      )}
      {q.isLoading && <div className="text-dim text-sm">{t('loading')}</div>}
      {!q.isLoading && !list.length && <div className="card p-6 text-dim text-sm">{t('t.none')}</div>}
      <ul className="space-y-3">
        {list.map((x) => (
          <li key={x.id}>
            <Link to={`/tournaments/${x.id}`} className="card block p-4 hover:border-ink-600 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{x.name}</div>
                  <div className="text-sm text-dim">{fmtDate(x.startsAt, lang)}{x.venue ? ` · ${x.venue}` : ''}</div>
                </div>
                <span className={`pill ${STATUS_CLS[x.status]} shrink-0`}>{t(`t.status.${x.status}`)}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="pill bg-ink-800 text-dim">{t(`t.format.${x.format}`)}</span>
                <span className="pill bg-ink-800 text-dim">{t('t.players', { n: x.confirmed, max: x.maxPlayers })}</span>
                {x.waitlisted > 0 && <span className="pill bg-ink-800 text-dim">{t('t.waitlist', { n: x.waitlisted })}</span>}
                {x.myStatus === 'in' && <span className="pill bg-ball-500/15 text-ball-300">{t('t.you.in')}</span>}
                {x.myStatus === 'waitlist' && <span className="pill bg-court-500/15 text-court-400">{t('t.you.waitlist')}</span>}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}

function localDefault(): string {
  const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(19, 0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function TournamentForm({ initial, frozen = false, busy, error, onSubmit, onCancel }: {
  initial?: Partial<TournamentInput>; frozen?: boolean; busy: boolean; error?: string;
  onSubmit: (i: TournamentInput) => void; onCancel?: () => void;
}) {
  const { t } = useT();
  const [f, setF] = useState<TournamentInput>({
    name: '', format: 'americano', venue: '', startsAt: localDefault(), courts: 2, rounds: 7, maxPlayers: 8, gamesPerMatch: 7,
    costPerGameCents: 200, handicapEnabled: true, handicapMax: 2, notes: '', ...initial,
    ...(initial?.startsAt ? { startsAt: toLocalInput(initial.startsAt) } : {}),
  });
  const set = <K extends keyof TournamentInput>(k: K, v: TournamentInput[K]) => setF((s) => ({ ...s, [k]: v }));
  const num = (k: keyof TournamentInput) => (e: React.ChangeEvent<HTMLInputElement>) => set(k, Number(e.target.value) as never);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({ ...f, startsAt: new Date(f.startsAt).toISOString() });
  }

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <div className="sm:col-span-2 lg:col-span-3"><label className="label">{t('t.f.name')}</label><input value={f.name} onChange={(e) => set('name', e.target.value)} required /></div>
      <div><label className="label">{t('t.f.format')}</label>
        <select value={f.format} disabled={frozen} onChange={(e) => set('format', e.target.value as TournamentFormat)}>
          <option value="americano">{t('t.format.americano')}</option><option value="mexicano">{t('t.format.mexicano')}</option>
        </select></div>
      <div><label className="label">{t('t.f.start')}</label><input type="datetime-local" value={f.startsAt} onChange={(e) => set('startsAt', e.target.value)} required /></div>
      <div><label className="label">{t('t.f.venue')}</label><input value={f.venue ?? ''} onChange={(e) => set('venue', e.target.value)} placeholder={t('optional')} /></div>
      <div><label className="label">{t('t.f.courts')}</label><input type="number" min={1} max={20} value={f.courts} disabled={frozen} onChange={num('courts')} /></div>
      <div><label className="label">{t('t.f.rounds')}</label><input type="number" min={1} max={30} value={f.rounds} disabled={frozen} onChange={num('rounds')} /></div>
      <div><label className="label">{t('t.f.max')}</label><input type="number" min={4} max={200} value={f.maxPlayers} disabled={frozen} onChange={num('maxPlayers')} /></div>
      <div><label className="label">{t('t.f.games')}</label><input type="number" min={1} max={50} value={f.gamesPerMatch} disabled={frozen} onChange={num('gamesPerMatch')} /></div>
      <div><label className="label">{t('t.f.cost')}</label>
        <input type="number" min={0} step={0.5} value={f.costPerGameCents / 100} onChange={(e) => set('costPerGameCents', Math.round(Number(e.target.value) * 100))} /></div>
      <div><label className="label">{t('t.f.hcMax')}</label><input type="number" min={0} max={6} value={f.handicapMax} disabled={frozen} onChange={num('handicapMax')} /></div>
      <label className="flex items-center gap-2 text-sm self-end pb-2.5"><input type="checkbox" checked={f.handicapEnabled} disabled={frozen} onChange={(e) => set('handicapEnabled', e.target.checked)} /> {t('t.f.hcEnabled')}</label>
      <div className="sm:col-span-2 lg:col-span-3"><label className="label">{t('t.f.notes')}</label><textarea rows={2} value={f.notes ?? ''} onChange={(e) => set('notes', e.target.value)} placeholder={t('optional')} /></div>
      {frozen && <div className="sm:col-span-2 lg:col-span-3 text-xs text-dim">{t('t.frozen')}</div>}
      {error && <div className="sm:col-span-2 lg:col-span-3"><Notice kind="error">{error}</Notice></div>}
      <div className="sm:col-span-2 lg:col-span-3 flex gap-2">
        <button className="btn-primary" disabled={busy}>{busy ? t('t.creating') : (initial ? t('save') : t('t.create'))}</button>
        {onCancel && <button type="button" className="btn-ghost" onClick={onCancel}>{t('cancel')}</button>}
      </div>
    </form>
  );
}

function toLocalInput(iso: string): string {
  const d = new Date(iso); const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
